import { Tooltip, rem } from '@mantine/core';
import {
  Avatar,
  Container,
  ExtraAvatar,
  Footer,
  People,
  Header,
  Link,
  LinkSection,
  Location,
  Title,
  Time,
  EventIndicator,
  MonthTime,
  MonthTitle,
  Content,
  ContentValue,
  GroupName,
  YearTime,
  YearEventIndicator,
  YearTitle,
  AllDayTitle,
  LineHeader,
  HeaderTop,
  ItemName,
  ListEventContainer,
  ListEventContent,
  ListEventPeople,
  ListEventHeader,
  Status,
  Value,
  LocationText,
  StatusContainer,
  ContentWrap,
  PeopleText,
  PeopleTextValue,
  IconSubitem,
} from './SingleEventStyle';
import { EventContentArg } from '@fullcalendar/core';
import { IconMapPin, IconSubtask } from '@tabler/icons-react';
import { memo } from 'react';
import EventTooltip from './EventTooltip/EventTooltip';

interface SingleEventPropsType extends EventContentArg {
  isAllDayEventData: boolean;
  groupSelection: { color: boolean; title: boolean };
}

const SingleEvent = memo((props: SingleEventPropsType) => {
  const isSubitem = props.event.extendedProps?.data?.parentItem ? true : false;

  const renderExtraPersons = () => {
    const extraPersons = props.event.extendedProps.data?.people
      .filter((el: any, i: number) => el && i >= 5)
      .map((el: any) => el?.name);
    const concatenateExtraPersonsNames = extraPersons.join(', ');
    return (
      <Tooltip
        label={concatenateExtraPersonsNames}
        withArrow
        transitionProps={{ transition: 'rotate-right' }}
      >
        <ExtraAvatar className="photo-thumb">+{extraPersons?.length}</ExtraAvatar>
      </Tooltip>
    );
  };

  const renderMonthPartDayEvent = () => {
    return (
      <Container
        $groupColor={props.groupSelection.color && props.event.extendedProps?.data?.group?.color}
      >
        <Tooltip
          label={<EventTooltip props={props} isSubitem={isSubitem} />}
          withArrow
          transitionProps={{ transition: 'rotate-right' }}
          maw={400}
          miw={200}
        >
          <LineHeader>
            <EventIndicator $color={props.backgroundColor} $isAllDay={props.event.allDay} />
            <MonthTime>{props.timeText}</MonthTime>
            {isSubitem && <IconSubitem stroke={1.5} />}
            <MonthTitle $isSubitem={isSubitem}>
              {props.event.title}
              {isSubitem && (
                <ItemName>{' | ' + props.event.extendedProps?.data?.parentItem?.name}</ItemName>
              )}
            </MonthTitle>
          </LineHeader>
        </Tooltip>
      </Container>
    );
  };

  const renderYearPartDayEvent = () => {
    return (
      <Container
        $groupColor={props.groupSelection.color && props.event.extendedProps?.data?.group?.color}
      >
        <Tooltip
          label={<EventTooltip props={props} isSubitem={isSubitem} />}
          withArrow
          transitionProps={{ transition: 'rotate-right' }}
          maw={400}
          miw={200}
        >
          <LineHeader>
            <YearEventIndicator $color={props.backgroundColor} $isAllDay={props.event.allDay} />
            <YearTime>{props.timeText}</YearTime>
            {isSubitem && (
              <IconSubtask
                stroke={1.5}
                style={{ minWidth: '13px', maxWidth: '13px', height: '13px', marginRight: '2px' }}
              />
            )}
            <YearTitle>
              {props.event.title}
              {isSubitem && (
                <ItemName>{' | ' + props.event.extendedProps?.data?.parentItem?.name}</ItemName>
              )}
            </YearTitle>
          </LineHeader>
        </Tooltip>
      </Container>
    );
  };

  const renderAllDayEvent = () => {
    return (
      <Container
        $groupColor={props.groupSelection.color && props.event.extendedProps?.data?.group?.color}
      >
        <Tooltip
          label={<EventTooltip props={props} isSubitem={isSubitem} />}
          withArrow
          transitionProps={{ transition: 'rotate-right' }}
          maw={400}
          miw={200}
        >
          <LineHeader>
            {isSubitem && <IconSubitem stroke={1.5} />}
            <AllDayTitle $isSubitem={isSubitem}>
              {props.event.title}
              {isSubitem && (
                <ItemName>{' | ' + props.event.extendedProps?.data?.parentItem?.name}</ItemName>
              )}
            </AllDayTitle>
          </LineHeader>
        </Tooltip>
      </Container>
    );
  };

  const renderPartDayEvent = () => {
    return (
      <Container
        $groupColor={props.groupSelection.color && props.event.extendedProps?.data?.group?.color}
      >
        <Tooltip
          label={<EventTooltip props={props} isSubitem={isSubitem} />}
          withArrow
          transitionProps={{ transition: 'rotate-right' }}
          maw={350}
          miw={200}
        >
          <Header
            $isShort={props.event.extendedProps.data.isShort}
            $isAllDay={props.event.allDay}
            $isAllDayEventData={props.isAllDayEventData}
          >
            <HeaderTop>
              <Time>{props.timeText}</Time>
            </HeaderTop>
            <Title>
              {isSubitem && (
                <span style={{ verticalAlign: 'middle' }}>
                  <IconSubitem stroke={1.5} />
                </span>
              )}
              {props.event.title}
              {isSubitem && (
                <ItemName>{' | ' + props.event.extendedProps?.data?.parentItem?.name}</ItemName>
              )}
            </Title>
          </Header>
        </Tooltip>
        <Content>
          {props.groupSelection.title && (
            <GroupName>{props.event.extendedProps?.data?.group?.title}</GroupName>
          )}
          <ContentWrap>
            {props.event.extendedProps?.data?.status && (
              <StatusContainer
                $isBackgroundColor={
                  props.backgroundColor === props.event.extendedProps?.data?.status?.color
                }
                $color={props.event.extendedProps?.data?.status?.color}
              >
                <Status>{props.event.extendedProps?.data?.status?.text}</Status>
              </StatusContainer>
            )}
            {props.event.extendedProps?.data?.text && (
              <Tooltip
                label={props.event.extendedProps?.data?.text}
                withArrow
                transitionProps={{ transition: 'rotate-right' }}
                multiline
                maw={350}
              >
                <ContentValue>
                  <Value>{props.event.extendedProps?.data?.text}</Value>
                </ContentValue>
              </Tooltip>
            )}
            {props.event.extendedProps?.data?.numbers && (
              <ContentValue>
                <Value>{props.event.extendedProps?.data?.numbers}</Value>
              </ContentValue>
            )}
            {props.event.extendedProps?.data?.email && (
              <ContentValue>
                <Value>{props.event.extendedProps?.data?.email}</Value>
              </ContentValue>
            )}
            {props.event.extendedProps?.data?.phone && (
              <ContentValue>
                <Value>{props.event.extendedProps?.data?.phone}</Value>
              </ContentValue>
            )}
            {props.event.extendedProps?.data?.timeline && (
              <ContentValue>
                <Value>{props.event.extendedProps?.data?.timeline}</Value>
              </ContentValue>
            )}
            {props.event.extendedProps?.data?.formula && (
              <Tooltip
                label={props.event.extendedProps?.data?.formula}
                withArrow
                transitionProps={{ transition: 'rotate-right' }}
                multiline
                maw={350}
              >
                <ContentValue>
                  <Value>{props.event.extendedProps?.data?.formula}</Value>
                </ContentValue>
              </Tooltip>
            )}
            {props.event.extendedProps?.data?.link && (
              <ContentValue>
                <Value>
                  <LinkSection>
                    <Tooltip
                      label={props.event.extendedProps.data.link?.text}
                      withArrow
                      transitionProps={{ transition: 'rotate-right' }}
                    >
                      <Link
                        target="_blank"
                        href={props.event.extendedProps.data.link?.url}
                        onClick={(e) => e.stopPropagation()}
                        rel="noreferrer"
                      >
                        {props.event.extendedProps.data.link?.text}
                      </Link>
                    </Tooltip>
                  </LinkSection>
                </Value>
              </ContentValue>
            )}
            {props.event.extendedProps?.data?.location && (
              <ContentValue>
                <Value>
                  <Location>
                    <IconMapPin
                      style={{
                        width: '13px',
                        minWidth: '13px',
                        height: '13px',
                        marginRight: '2px',
                      }}
                    />
                    <LocationText>{props.event.extendedProps.data.location}</LocationText>
                  </Location>
                </Value>
              </ContentValue>
            )}
          </ContentWrap>
        </Content>
        <Footer>
          {typeof props.event.extendedProps.data?.people === 'string' ? (
            props.event.extendedProps.data?.people && (
              <PeopleText>
                <ContentValue>
                  <PeopleTextValue>{props.event.extendedProps.data?.people}</PeopleTextValue>
                </ContentValue>
              </PeopleText>
            )
          ) : (
            <People>
              {props.event.extendedProps.data?.people?.length &&
                props.event.extendedProps.data.people.map((guest: any, index: number) => {
                  if (guest && index < 5) {
                    return (
                      <Tooltip
                        key={guest?.id}
                        label={guest?.name}
                        withArrow
                        transitionProps={{ transition: 'rotate-right' }}
                      >
                        <Avatar className="photo-thumb" src={guest.photo_thumb} />
                      </Tooltip>
                    );
                  }
                  return null;
                })}
              {props.event.extendedProps.data?.people?.length > 5 && renderExtraPersons()}
            </People>
          )}
        </Footer>
      </Container>
    );
  };

  const renderListEvent = () => {
    return (
      <ListEventContainer
        $groupColor={props.groupSelection.color && props.event.extendedProps?.data?.group?.color}
      >
        <ListEventHeader>
          <Tooltip
            label={`${props.event.title} ${
              isSubitem ? ' | ' + props.event.extendedProps?.data?.parentItem?.name : ''
            }`}
            withArrow
            transitionProps={{ transition: 'rotate-right' }}
            multiline
            maw={350}
          >
            <Title>
              {isSubitem && (
                <span style={{ verticalAlign: 'middle' }}>
                  <IconSubitem stroke={1.5} />
                </span>
              )}
              {props.event.title}
              {isSubitem && (
                <ItemName>{' | ' + props.event.extendedProps?.data?.parentItem?.name}</ItemName>
              )}
            </Title>
          </Tooltip>
          {props.groupSelection.title && (
            <GroupName>{props.event.extendedProps?.data?.group?.title}</GroupName>
          )}
        </ListEventHeader>
        <ListEventContent>
          {props.event.extendedProps?.data?.status && (
            <StatusContainer
              $isBackgroundColor={
                props.backgroundColor === props.event.extendedProps?.data?.status?.color
              }
              $color={props.event.extendedProps?.data?.status?.color}
            >
              <Status>{props.event.extendedProps?.data?.status?.text}</Status>
            </StatusContainer>
          )}
          {props.event.extendedProps?.data?.text && (
            <ContentValue>
              <Value>{props.event.extendedProps?.data?.text}</Value>
            </ContentValue>
          )}
          {props.event.extendedProps?.data?.numbers && (
            <ContentValue>
              <Value>{props.event.extendedProps?.data?.numbers}</Value>
            </ContentValue>
          )}
          {props.event.extendedProps?.data?.email && (
            <ContentValue>
              <Value>{props.event.extendedProps?.data?.email}</Value>
            </ContentValue>
          )}
          {props.event.extendedProps?.data?.phone && (
            <ContentValue>
              <Value>{props.event.extendedProps?.data?.phone}</Value>
            </ContentValue>
          )}
          {props.event.extendedProps?.data?.timeline && (
            <ContentValue>
              <Value>{props.event.extendedProps?.data?.timeline}</Value>
            </ContentValue>
          )}
          {props.event.extendedProps?.data?.formula && (
            <ContentValue>
              <Value>{props.event.extendedProps?.data?.formula}</Value>
            </ContentValue>
          )}
          {props.event.extendedProps?.data?.link && (
            <ContentValue>
              <Value>
                <LinkSection>
                  <Tooltip
                    label={props.event.extendedProps.data.link?.text}
                    withArrow
                    transitionProps={{ transition: 'rotate-right' }}
                  >
                    <Link
                      target="_blank"
                      href={props.event.extendedProps.data.link?.url}
                      onClick={(e) => e.stopPropagation()}
                      rel="noreferrer"
                    >
                      {props.event.extendedProps.data.link?.text}
                    </Link>
                  </Tooltip>
                </LinkSection>
              </Value>
            </ContentValue>
          )}
          {props.event.extendedProps?.data?.location && (
            <ContentValue>
              <Value>
                <Location>
                  <IconMapPin
                    style={{
                      width: rem(13),
                      minWidth: rem(13),
                      height: rem(13),
                      marginRight: '2px',
                    }}
                  />
                  <LocationText>{props.event.extendedProps.data.location}</LocationText>
                </Location>
              </Value>
            </ContentValue>
          )}
          <Footer>
            {typeof props.event.extendedProps.data?.people === 'string' ? (
              props.event.extendedProps.data?.people && (
                <PeopleText>
                  <ContentValue>
                    <PeopleTextValue>{props.event.extendedProps.data?.people}</PeopleTextValue>
                  </ContentValue>
                </PeopleText>
              )
            ) : (
              <ListEventPeople>
                {props.event.extendedProps.data?.people?.length &&
                  props.event.extendedProps.data.people.map((guest: any, index: number) => {
                    if (guest && index < 5) {
                      return (
                        <Tooltip
                          key={guest?.id}
                          label={guest?.name}
                          withArrow
                          transitionProps={{ transition: 'rotate-right' }}
                        >
                          <Avatar className="photo-thumb" src={guest.photo_thumb} />
                        </Tooltip>
                      );
                    }
                    return null;
                  })}
                {props.event.extendedProps.data?.people?.length > 5 && renderExtraPersons()}
              </ListEventPeople>
            )}
          </Footer>
        </ListEventContent>
      </ListEventContainer>
    );
  };

  const renderEvent = () => {
    switch (true) {
      case props.event.allDay && !props.isAllDayEventData && props.view.type !== 'listWeek':
      case props.view.type === 'dayGridMonth' && props.event.allDay:
      case props.view.type === 'multiMonthYear' && props.event.allDay:
        return renderAllDayEvent();
      case props.view.type === 'dayGridMonth' && !props.event.allDay:
        return renderMonthPartDayEvent();
      case props.view.type === 'multiMonthYear' && !props.event.allDay:
        return renderYearPartDayEvent();
      case props.view.type === 'listWeek':
        return renderListEvent();
      default:
        return renderPartDayEvent();
    }
  };

  return renderEvent();
});

export default SingleEvent;
